if (!customElements.get('product-sticky-menu')) {
  class ProductStickyMenu extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.buttons = Array.from(this.querySelectorAll('[data-pdp-menu-trigger]'));
      this.backToTopButton = this.querySelector('[data-pdp-back-to-top]');
      this.hideMissingTargets = this.dataset.hideMissingTargets === 'true';
      this.targets = [];
      this.scrollTicking = false;
      this.onBackToTopClick = () => this.scrollToTop();

      this.refreshTargets();
      this.mountBackToTopButton();
      this.bindEvents();
      this.setActiveItem();
      this.setBackToTopVisibility();
    }

    bindEvents() {
      this.buttons.forEach((button) => {
        button.addEventListener('click', () => this.scrollToTarget(button));
      });

      if (this.backToTopButton) {
        this.backToTopButton.addEventListener('click', this.onBackToTopClick);
      }

      window.addEventListener('scroll', () => this.requestActiveUpdate(), { passive: true });
      window.addEventListener('resize', () => {
        this.refreshTargets();
        this.requestActiveUpdate();
      });

      const observer = new MutationObserver(() => {
        this.refreshTargets();
        this.requestActiveUpdate();
      });

      observer.observe(document.body, { childList: true, subtree: true });
      this.observer = observer;
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.backToTopButton) {
        this.backToTopButton.removeEventListener('click', this.onBackToTopClick);

        if (this.backToTopButton.parentElement === document.body) {
          this.backToTopButton.remove();
        }
      }
    }

    refreshTargets() {
      this.targets = [];

      this.buttons.forEach((button) => {
        const target = this.resolveTarget(button);
        const item = button.closest('.pdp-sticky-menu__item');

        if (target) {
          button.disabled = false;
          button.removeAttribute('aria-disabled');
          button._pdpTarget = target;
          if (item) item.hidden = false;
          this.targets.push({ button, target });
        } else {
          button._pdpTarget = null;

          if (this.hideMissingTargets) {
            if (item) item.hidden = true;
          } else {
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
          }
        }
      });

      this.hidden = this.targets.length === 0;
    }

    mountBackToTopButton() {
      if (!this.backToTopButton || this.backToTopButton.parentElement === document.body) return;
      document.body.appendChild(this.backToTopButton);
    }

    resolveTarget(button) {
      const key = button.dataset.pdpMenuTarget;
      const customSelector = button.dataset.pdpMenuCustomSelector;

      if (key === 'reviews') return this.resolveReviewsTarget();

      const selectors = key === 'custom_selector' ? [customSelector] : ProductStickyMenu.targetSelectors[key] || [];

      for (const selector of selectors) {
        if (!selector) continue;

        let node = null;

        try {
          node = document.querySelector(selector);
        } catch (error) {
          node = null;
        }

        if (!node) continue;

        const target = node.closest('.shopify-section') || node;
        if (this.isUsableTarget(target)) return target;
      }

      return null;
    }

    resolveReviewsTarget() {
      const marker = document.getElementById('all-reviews');
      const reviewWidget = this.findReviewWidgetAfter(marker) || this.findReviewWidget();

      if (reviewWidget) {
        const reviewSection = reviewWidget.closest('.shopify-section') || reviewWidget;
        if (this.isUsableTarget(reviewSection)) return reviewSection;
        if (this.isUsableTarget(reviewWidget)) return reviewWidget;
      }

      if (marker) {
        const markerSection = marker.closest('.shopify-section');
        const nextSection = this.findNextUsableSection(markerSection);
        if (nextSection) return nextSection;

        if (!marker.hidden && marker.getAttribute('aria-hidden') !== 'true') return marker;
      }

      return null;
    }

    findReviewWidgetAfter(marker) {
      if (!marker) return null;

      return ProductStickyMenu.reviewSelectors
        .flatMap((selector) => Array.from(document.querySelectorAll(selector)))
        .find((node) => {
          if (!this.isFullReviewWidget(node)) return false;
          return Boolean(marker.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING);
        }) || null;
    }

    findReviewWidget() {
      return ProductStickyMenu.reviewSelectors
        .flatMap((selector) => Array.from(document.querySelectorAll(selector)))
        .find((node) => this.isFullReviewWidget(node)) || null;
    }

    isFullReviewWidget(node) {
      if (!node) return false;

      return !node.closest(
        '.product-details__reviews-preview, .jdgm-preview-badge, [data-widget-name="preview_badge"]'
      );
    }

    findNextUsableSection(section) {
      let next = section ? section.nextElementSibling : null;

      while (next) {
        if (next.matches && next.matches('.shopify-section') && this.isUsableTarget(next)) return next;
        next = next.nextElementSibling;
      }

      return null;
    }

    isUsableTarget(target) {
      const ownSection = this.closest('.shopify-section');
      if (!target || target === ownSection || target.hidden || target.getAttribute('aria-hidden') === 'true') return false;

      if (target.id === 'all-reviews') return true;

      const rects = target.getClientRects();
      if (!rects.length) return false;
      if (target.offsetHeight <= 1 && target.textContent.trim() === '') return false;

      return true;
    }

    scrollToTarget(button) {
      const target = button._pdpTarget || this.resolveTarget(button);
      if (!target) return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - this.getScrollOffset());

      window.scrollTo({
        top,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });

      this.setActiveButton(button);
    }

    requestActiveUpdate() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;

      window.requestAnimationFrame(() => {
        this.scrollTicking = false;
        this.setActiveItem();
        this.setBackToTopVisibility();
      });
    }

    setActiveItem() {
      if (!this.targets.length) return;

      const offset = this.getScrollOffset() + 4;
      let active = this.targets[0];

      this.targets.forEach((entry) => {
        const rect = entry.target.getBoundingClientRect();
        const top = rect.top + window.scrollY;

        if (window.scrollY + offset >= top) {
          active = entry;
        }
      });

      this.setActiveButton(active.button);
    }

    setActiveButton(activeButton) {
      this.buttons.forEach((button) => {
        const isActive = button === activeButton;
        button.classList.toggle('is-active', isActive);

        if (isActive) {
          button.setAttribute('aria-current', 'true');
          this.scrollButtonIntoView(button);
        } else {
          button.removeAttribute('aria-current');
        }
      });
    }

    scrollButtonIntoView(button) {
      const inner = this.querySelector('.pdp-sticky-menu__inner');
      if (!inner) return;

      const buttonRect = button.getBoundingClientRect();
      const innerRect = inner.getBoundingClientRect();

      if (buttonRect.left < innerRect.left || buttonRect.right > innerRect.right) {
        button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }

    setBackToTopVisibility() {
      if (!this.backToTopButton) return;

      const shouldShow = !this.hidden && window.scrollY > this.getBackToTopThreshold();

      this.classList.toggle('pdp-sticky-menu--show-back-to-top', shouldShow);
      this.backToTopButton.classList.toggle('is-visible', shouldShow);
      this.backToTopButton.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      this.backToTopButton.tabIndex = shouldShow ? 0 : -1;
    }

    getBackToTopThreshold() {
      return window.matchMedia('(max-width: 767px)').matches ? 700 : 1000;
    }

    scrollToTop() {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const isDesktop = this.isDesktopProductLayout();
      const target = this.getBackToTopTarget(isDesktop);
      if (!target) return;

      const top = Math.max(
        0,
        window.scrollY + target.getBoundingClientRect().top - this.getBackToTopOffset(isDesktop)
      );

      window.scrollTo({
        top,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });

      if (isDesktop) this.resetMainProductColumns(prefersReducedMotion);
    }

    isDesktopProductLayout() {
      if (window.theme && window.theme.mediaMatches && typeof window.theme.mediaMatches.md === 'boolean') {
        return window.theme.mediaMatches.md;
      }

      return window.matchMedia('(min-width: 769px)').matches;
    }

    getBackToTopTarget(isDesktop) {
      if (document.body.classList.contains('halloween-bundle-page')) {
        return document.getElementById('main-content');
      }

      if (isDesktop) {
        return document.querySelector('.cc-main-product.product-main') ||
          document.querySelector('.product-main');
      }

      return document.querySelector('#product-media .media-gallery__viewer') ||
        document.querySelector('#product-media .media-gallery') ||
        document.getElementById('product-media') ||
        document.querySelector('.product-main .product-media') ||
        document.querySelector('.cc-main-product.product-main') ||
        document.querySelector('.product-main');
    }

    getBackToTopOffset(isDesktop) {
      const rootStyle = getComputedStyle(document.documentElement);
      const headerOffset = parseFloat(rootStyle.getPropertyValue('--pmdtc-sticky-header-height')) ||
        parseFloat(rootStyle.getPropertyValue('--header-end-padded')) ||
        parseFloat(rootStyle.getPropertyValue('--header-end')) ||
        0;

      return headerOffset + (isDesktop ? 0 : 8);
    }

    resetMainProductColumns(prefersReducedMotion) {
      [
        document.querySelector('.product-main .product-media-column'),
        document.querySelector('.product-main .product-info')
      ].forEach((column) => {
        if (!column || column.scrollHeight <= column.clientHeight) return;

        column.scrollTo({
          top: 0,
          behavior: prefersReducedMotion ? 'auto' : 'smooth'
        });
      });
    }

    getScrollOffset() {
      const rootStyle = getComputedStyle(document.documentElement);
      const headerOffset = parseFloat(rootStyle.getPropertyValue('--pmdtc-sticky-header-height')) ||
        parseFloat(rootStyle.getPropertyValue('--header-end-padded')) ||
        0;
      const menuHeight = this.getBoundingClientRect().height || 0;
      const extraOffset = Number(this.dataset.scrollExtraOffset) || 0;

      return headerOffset + menuHeight + extraOffset;
    }
  }

  ProductStickyMenu.targetSelectors = {
    product_banners: ['.cc-product-banner-list'],
    product_information_links: ['.cc-icons-with-text-sds-tds'],
    print_condition_strip: ['.pm-printing-tips', '.pm-print-condition-strip'],
    printing_tips: ['.pm-printing-tips'],
    material_properties: ['.pm-material-properties'],
    material_compare: ['.cc-product-material-compare'],
    community_prints: ['.cc-product-community-prints'],
    product_faq: ['.cc-product-faq'],
    reviews: [],
    recommendations: ['product-recommendations']
  };

  ProductStickyMenu.reviewSelectors = ['#judgeme_product_reviews', '.jdgm-review-widget'];

  customElements.define('product-sticky-menu', ProductStickyMenu);
}
